import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import useProcessingTypeApi from "../../http/processingType/api";
import { ProcessingType, UpsertProcessingType } from "../../http/processingType/type";
import { PaginationQuery } from "../../types";
import { formatViewName } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import BlueCheck from "../ui/BlueCheck";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import RedCross from "../ui/RedCross";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import ProcessingTypeForm, { ProcessingTypeFormBody } from "./ProcessingTypeForm";

const ProcessingTypes: FC = () => {
  const { useProcessingTypes: getProcessingTypes } = useProcessingTypeApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { isOpen, open, close } = useToggle();
  const [editType, setEditType] = useState<ProcessingType>();
  const [deleteType, setDeleteType] = useState<ProcessingType>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const processingTypeApi = useProcessingTypeApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const pageContext = usePageContext();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = getProcessingTypes(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.common.types });
  }, [i18n.language]);

  const onSubmit = async (formData: ProcessingTypeFormBody) => {
    setIsFormSubmitting(true);
    const body: UpsertProcessingType = {
      name: formData.name,
      // isKitRequest: formData.isKitRequest,
      showMarketingFields: formData.showMarketingFields,
      allowQuantityVariation: formData.allowQuantityVariation,
      initialItemIsFinalItem: formData.initialItemIsFinalItem,
      views: formData.views,
    };
    if (!editType) {
      try {
        await processingTypeApi.createProcessingType(body);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      try {
        await processingTypeApi.updateProcessingType(body, editType.id);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
    setIsFormSubmitting(false);
  };

  const actions: Action<ProcessingType>[] = [
    {
      title: i18n.t.common.edit,
      onClick: (item) => {
        setEditType(item);
        open();
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: setDeleteType,
      icon: SvgDelete,
      color: "red",
      isDivider: true,
    },
  ];

  const filterOptions = {
    get: processingTypeApi.getFieldFiltersQuery,
    update: filters.setFilters,
    getSelectedOptions: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        onClose={close}
        title={!editType ? i18n.t.processingRequestTypes.newType : i18n.t.processingRequestTypes.editType}
      >
        <ProcessingTypeForm
          processingType={editType}
          onSubmit={onSubmit}
          key={isOpen ? (editType?.id ?? "new") : "closed"}
          isLoading={isFormSubmitting}
        />
      </Sidebar>

      <Table<ProcessingType>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  setEditType(undefined);
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        rowActions={actions}
        onRowClick={(item) => {
          setEditType(item);
          open();
        }}
        filters={filters}
        head={
          <tr>
            <TH order={orderOptions} fieldName="processingType.name" title={i18n.t.common.name} />
            <TH
              order={orderOptions}
              filters={filterOptions}
              fieldName="processingType.showMarketingFields"
              title={i18n.t.common.showsMarketingFields}
            />
            <TH
              order={orderOptions}
              filters={filterOptions}
              fieldName="processingType.allowQuantityVariation"
              title={i18n.t.common.allowQuantityVariation}
            />
            <TH
              order={orderOptions}
              filters={filterOptions}
              fieldName="processingType.initialItemIsFinalItem"
              title={i18n.t.common.initialItemIsFinalItem}
            />
            <TH order={orderOptions} fieldName="processingType.views" title={i18n.t.common.views} />
            <th align="left"></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.name}</td>
            {/* <td>{item.isKitRequest ? <BlueCheck /> : <RedCross />}</td> */}
            <td>{item.showMarketingFields ? <BlueCheck /> : <RedCross />}</td>
            <td>{item.allowQuantityVariation ? <BlueCheck /> : <RedCross />}</td>
            <td>{item.initialItemIsFinalItem ? <BlueCheck /> : <RedCross />}</td>
            <td>
              {item.views.map((view) => (
                <div key={view}>{formatViewName(view, i18n)}</div>
              ))}
            </td>
          </>
        )}
        items={data?.items}
        total={data?.total}
        error={error}
      />
      {!!deleteType && (
        <ModalDialog
          onClose={() => setDeleteType(undefined)}
          title={i18n.t.processingRequestTypes.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeleteType(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deleteType) return;
                try {
                  await processingTypeApi.deleteProcessingType(deleteType.id);
                  setIsModalSubmitting(false);
                  setDeleteType(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.processingRequestTypes.questions.delete}
        </ModalDialog>
      )}
    </>
  );
};

export default ProcessingTypes;
